import React, { useState } from 'react';
import { Box, Button, Divider, Theme } from '@material-ui/core';
import { BorderColorOutlined, ChatBubble, Email, ExpandMore, NotificationsActive } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import Icon from '@mdi/react';
import { mdiChat } from '@mdi/js';
import { TooltipMenu } from '../../../../../common/tooltip-menu';
import { useOrgInfo } from '../../../../../global/auth/use-org-info/use-org-info';
import { VolunteerCommonProfileEmailDialog } from './VolunteerCommonProfileEmailDialog';
import { VolunteerCommonProfileSmsDialog } from './VolunteerCommonProfileSmsDialog';

export interface CommunicateRecipientProfile {
  profileId: string;
  preferredName: string;
  lastName: string;
  contactNumber?: string | null;
  avatarUrl?: string | null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuItem: {
      color: theme.alert.blue.extraLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: 500,
      '& span': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
      textTransform: 'none',
    },
    menuDivider: {
      width: '100%',
      margin: '4px 0',
    },
  })
);

interface CommunicateButtonProps {
  profile?: CommunicateRecipientProfile | null;
  volunteerId?: string;
  variant?: 'contained' | 'outlined';
  dataTrack?: string;
  buttonStyle?: React.CSSProperties;
  onSendReminder?: () => void;
  onSent?: () => void;
  allowRecipientSelection?: boolean;
  onNewBulletin?: () => void;
}

export const CommunicateButton = ({
  profile,
  volunteerId,
  variant = 'contained',
  dataTrack = 'fs-volPrf-menu-action-menu-button',
  buttonStyle,
  onSendReminder,
  onSent,
  allowRecipientSelection = false,
  onNewBulletin,
}: CommunicateButtonProps) => {
  const classes = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const orgInfo = useOrgInfo();

  if (!profile && !onSendReminder && !allowRecipientSelection && !onNewBulletin) return null;

  const recipient = profile && {
    volunteerId,
    profile: {
      profileId: profile.profileId,
      preferredName: profile.preferredName,
      lastName: profile.lastName,
      phone: profile.contactNumber ?? undefined,
      avatarUrl: profile.avatarUrl,
    },
  };

  const canCompose = Boolean(recipient) || allowRecipientSelection;
  const dialogRecipients = recipient || [];

  return (
    <>
      {canCompose && (
        <>
          <VolunteerCommonProfileEmailDialog
            volunteers={dialogRecipients}
            open={emailDialogOpen}
            onClose={() => setEmailDialogOpen(false)}
            onSent={onSent}
          />
          <VolunteerCommonProfileSmsDialog
            volunteers={dialogRecipients}
            open={smsDialogOpen}
            onClose={() => setSmsDialogOpen(false)}
            onSent={onSent}
          />
        </>
      )}
      <TooltipMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        title={
          <Box
            display='flex'
            flexDirection='column'
            alignItems='flex-start'
            style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
          >
            {onSendReminder && (
              <>
                <Button
                  className={classes.menuItem}
                  startIcon={<NotificationsActive />}
                  onClick={() => {
                    setMenuOpen(false);
                    onSendReminder();
                  }}
                >
                  Send Reminder
                </Button>
                {canCompose && <Divider className={classes.menuDivider} />}
              </>
            )}
            {canCompose && (
              <>
                <Button
                  className={classes.menuItem}
                  startIcon={<Email />}
                  onClick={() => {
                    setMenuOpen(false);
                    setEmailDialogOpen(true);
                  }}
                >
                  Email
                </Button>
                {orgInfo?.smsEnabled && (
                  <Button
                    className={classes.menuItem}
                    startIcon={<ChatBubble />}
                    onClick={() => {
                      setMenuOpen(false);
                      setSmsDialogOpen(true);
                    }}
                  >
                    SMS
                  </Button>
                )}
              </>
            )}
            {onNewBulletin && (
              <>
                {canCompose && <Divider className={classes.menuDivider} />}
                <Button
                  className={classes.menuItem}
                  startIcon={<BorderColorOutlined />}
                  onClick={() => {
                    setMenuOpen(false);
                    onNewBulletin();
                  }}
                >
                  Bulletin
                </Button>
              </>
            )}
          </Box>
        }
      >
        <TabletButton
          data-track={dataTrack}
          size='small'
          variant={variant}
          color={variant === 'contained' ? 'primary' : undefined}
          aria-controls='communicate-menu'
          aria-haspopup='true'
          onClick={() => setMenuOpen(true)}
          startIcon={<Icon path={mdiChat} horizontal style={{ height: '15px' }} />}
          endIcon={<ExpandMore />}
          style={{
            height: '100%',
            border: variant === 'contained' ? '1px solid transparent' : undefined,
            boxSizing: 'border-box',
            borderRadius: '4px',
            padding: '5px 10px',
            marginRight: '10px',
            color: variant === 'contained' ? '#fff' : undefined,
            ...buttonStyle,
          }}
        >
          Contact
        </TabletButton>
      </TooltipMenu>
    </>
  );
};
